import React, { useState } from "react";
import _ from "lodash";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Collapse,
  List,
  ListItemButton,
  Menu,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  useMediaQuery,
  Tab,
} from "@mui/material";
import { LocalizedLink as Link, useLocalization } from "gatsby-theme-i18n";
import { navigate } from "gatsby";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faChevronDown,
  faChevronUp,
  faSearch,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowDownLong,
  faArrowUpLong,
} from "@fortawesome/pro-light-svg-icons";
import { Trans } from "@lingui/macro";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import logoNegative from "../images/logo-negative.png";
import logoPositive from "../images/logo.png";
import aboutUs from "../images/menu-about-us.png";
import { getOptionsMenu, headers, restHeaders } from "../utils/navbar";
import Flag from "./Flag";
import { getCountryByLanguage } from "../utils/screen";
import share from "../services/share";
import { handleNotAvailable } from "../services/utils";
import { useScrollDirection } from "../hooks/useScrollDirection";
import Tabs from "./Tabs";
import { availablesLanguages } from "../utils/common";

function Navbar() {
  const _window = typeof window !== "undefined" && window;
  const { locale, config } = useLocalization();
  const isCollapsed = useMediaQuery((theme) => theme.breakpoints.down("md"), {
    noSsr: true,
  });
  const scrollDirection = useScrollDirection();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeHeader, setActiveHeader] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [data, setData] = React.useState({});
  const [showOptions, setShowOptions] = React.useState(false);
  const availablesLangs = config.filter(
    ({ code }) => code !== locale && availablesLanguages.includes(code),
  );

  // collapsed menu
  const [isOpenCollapsedMenu, setIsOpenCollapsedMenu] = useState(false);
  const contrastMenu = isHovered || isOpen;
  const currentSubMenus = _.get(headers(), `${activeHeader}.elements`, {});
  const landingMenuPage = _.get(headers(), `${activeHeader}`, {});
  const optionsMenu = getOptionsMenu();

  React.useEffect(() => {
    document.body.style.overflow = isOpenCollapsedMenu ? "hidden" : "auto";
    setIsHovered(_window?.pageYOffset > 100);
    _window?.addEventListener("scroll", () =>
      setIsHovered(_window?.pageYOffset > 0 && scrollDirection === "up"),
    );
  }, [_window, isOpenCollapsedMenu]);

  React.useEffect(() => {
    share()
      .loadTradingDetails()
      .then((td) => {
        const percentChange =
          (td.lastPrice / (td.previousClosePrice || td.lastPrice) - 1) * 100;
        setData({
          lastPrice: handleNotAvailable(td.lastPrice.toFixed(2)),
          percentChange: handleNotAvailable(percentChange.toFixed(2)),
        });
      });
  }, []);

  const updateMenu = (anchor, activeHeader) => {
    setAnchorEl(anchor);
    setActiveHeader(activeHeader);
  };

  const onCloseAll = () => {
    setIsOpen(false);
    setActiveSubmenu(null);
    setIsOpenCollapsedMenu(false);
    setShowOptions(false);
    setActiveHeader(null);
    setAnchorEl(null);
  };

  const goToPage = (path, state = {}) => {
    onCloseAll();
    navigate(`/${locale}${path}`, { state });
  };

  const renderCollapsedMenu = (
    elements = { ...headers(), ...restHeaders() },
    level = 0,
    landingPage = {},
  ) => (
    <List disablePadding>
      {landingPage.path && (
        <ListItemButton onClick={() => goToPage(landingPage.path)}>
          <CollapsedItem pl={level}>
            <Typography>
              <Trans>Home</Trans> {landingPage.label}
            </Typography>
          </CollapsedItem>
        </ListItemButton>
      )}
      {Object.entries(elements).map(([key, values]) => (
        <Box key={key} pl={level}>
          <ListItemButton
            sx={{ paddingX: "20px" }}
            selected={activeHeader?.includes(key)}
          >
            <CollapsedItem
              justifyContent="space-between"
              onClick={() => {
                if (!values.elements) return goToPage(values.path, values.state);
                const parts = activeHeader?.split(".") || [];
                parts[level] === key
                  ? parts.splice(level, 1)
                  : (parts[level] = key);
                return setActiveHeader(parts.join("."));
              }}
            >
              <Typography>{values.label}</Typography>
              {values.elements && (
                <Box width="20%" textAlign="end">
                  {!activeHeader?.includes(key) ? (
                    <FontAwesomeIcon icon={faChevronDown} />
                  ) : (
                    <FontAwesomeIcon icon={faChevronUp} />
                  )}
                </Box>
              )}
            </CollapsedItem>
          </ListItemButton>
          {values.elements && (
            <Collapse
              key={`list-${key}`}
              in={activeHeader?.includes(key)}
              timeout="auto"
              unmountOnExit
            >
              {renderCollapsedMenu(values.elements, level + 1, {
                label: values?.label,
                path: values?.path,
              })}
            </Collapse>
          )}
        </Box>
      ))}
    </List>
  );

  const renderSharePrice = () => {
    if (_.isEmpty(data)) return null;
    return (
      <SectionLink to={headers().investors.elements.share_information.path}>
        <ShareData display="flex" mx={{ sm: 2 }} isHovered={contrastMenu}>
          <Box>
            <Typography variant="small" fontWeight={600}>
              {data.lastPrice} €
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <FontAwesomeIcon
              style={{ height: "15px" }}
              icon={data.percentChange > 0 ? faArrowUpLong : faArrowDownLong}
            />
            <Typography ml={0.5} variant="comment">
              {data.percentChange}%
            </Typography>
          </Box>
        </ShareData>
      </SectionLink>
    );
  };

  return (
    <MenuContainer component="header">
      <Box
        className={`navbar ${scrollDirection === "down" ? "hidden" : ""}`}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => onCloseAll()}
      >
        <MenuCard
          activeSubmenu={activeSubmenu || false}
          isHovered={contrastMenu}
          isOpenCollapsedMenu={isOpenCollapsedMenu}
          isCollapsed={isCollapsed}
        >
          <Link className="logo" to="/">
            <img
              alt="allfunds_logo"
              src={contrastMenu ? logoNegative : logoPositive}
            />
          </Link>
          <StyledCollapsedMenu direction="row" spacing={1} alignItems="center">
            {renderSharePrice()}
            <FontAwesomeIcon
              icon={isOpenCollapsedMenu ? faXmark : faBars}
              color={contrastMenu ? "primary" : "white"}
              onClick={() => setIsOpenCollapsedMenu(!isOpenCollapsedMenu)}
              style={{ widht: "1.5rem", height: "1.5rem" }}
            />
          </StyledCollapsedMenu>
          <Box className="main">
            <Box>
              {renderSharePrice()}
              <Box className="top" isHovered={contrastMenu}>
                <Box component="nav" display="flex">
                  {Object.entries(headers())?.map(([key, values]) => (
                    <SelectedButton
                      onClick={(e) =>
                        (values.elements
                          ? updateMenu(e.currentTarget, key)
                          : goToPage(values.path))}
                      isSelected={activeHeader === key}
                    >
                      <Typography variant="comment">{values.label}</Typography>
                    </SelectedButton>
                  ))}
                </Box>
                {!showOptions ? (
                  <SelectedButton onClick={() => setShowOptions(!showOptions)}>
                    <FontAwesomeIcon icon={faSearch} />
                  </SelectedButton>
                ) : (
                  <Autocomplete
                    options={optionsMenu || []}
                    groupBy={(option) => option.group}
                    sx={{
                      width: 300,
                      "& input": {
                        height: "14px",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        border: (theme) =>
                          `1px solid ${theme.palette.neutral.main} !important`,
                      },
                    }}
                    renderInput={(params) => (
                      <TextField placeholder="Search menu" {...params} />
                    )}
                    onChange={(_, item) => navigate(`/${locale}${item.path}`)}
                    renderOption={(props, option, { inputValue }) => {
                      const matches = match(option.label, inputValue);
                      const parts = parse(option.label, matches);
                      return (
                        <Box component="li" {...props}>
                          <SectionLink to={`${option.path}`}>
                            <Box>
                              {parts.map((part, index) => (
                                <span
                                  key={index}
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: part.highlight && 700,
                                  }}
                                >
                                  {part.text}
                                </span>
                              ))}
                            </Box>
                          </SectionLink>
                        </Box>
                      );
                    }}
                    renderGroup={(params) => {
                      const title = params.group;
                      return (
                        <Stack
                          spacing={1}
                          sx={{
                            padding: (theme) =>
                              `${theme.spacing(1)} ${theme.spacing(0.5)}`,
                          }}
                        >
                          <Typography
                            sx={{ ml: 1 }}
                            color="secondary"
                            variant="small"
                          >
                            {title}
                          </Typography>
                          <StyledBox>{params.children}</StyledBox>
                        </Stack>
                      );
                    }}
                  />
                )}

                <Button onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <Flag
                    size="small"
                    country={getCountryByLanguage(locale) || locale}
                  />
                  <FontAwesomeIcon
                    style={{ marginLeft: "0.5rem" }}
                    icon={faChevronDown}
                  />
                </Button>

                <Menu
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => updateMenu(null, null)}
                  sx={{
                    "& .MuiPaper-root": {
                      background: (theme) => theme.palette.primary.main,
                      borderRadius: `${
                        activeHeader === "esg" ? 0 : 5
                      }px 5px 5px 5px`,
                    },
                  }}
                >
                  {!_.isEmpty(currentSubMenus) ? (
                    <Masonry columns={activeHeader === "esg" ? 2 : 3}>
                      {Object.values(currentSubMenus)?.map(
                        ({ label, path, elements = undefined }, index) => (
                          <>
                            {index === 0 && landingMenuPage.path && (
                              <Box p={2}>
                                <LinkedTypography
                                  path={landingMenuPage.path}
                                  onClick={() =>
                                    navigate(
                                      `/${locale}${landingMenuPage.path}`,
                                    )}
                                >
                                  <Trans>Home</Trans> {landingMenuPage.label}
                                </LinkedTypography>
                              </Box>
                            )}
                            <Box p={2}>
                              {label && (
                                <Stack
                                  spacing={1}
                                  sx={{
                                    paddingLeft: (theme) =>
                                      elements && theme.spacing(1),
                                    borderLeft: elements && "1px solid white",
                                  }}
                                >
                                  <LinkedTypography
                                    path={path}
                                    onClick={() => path && goToPage(path)}
                                  >
                                    {label}
                                  </LinkedTypography>
                                  {Object.values(elements || {})?.map(
                                    ({ label, path, state = {} }) => (
                                      <LinkedTypography
                                        fontSize="14px !important"
                                        fontWeight="400 !important"
                                        path={path}
                                        onClick={() => goToPage(path, state)}
                                      >
                                        {label}
                                      </LinkedTypography>
                                    ),
                                  )}
                                </Stack>
                              )}
                            </Box>
                          </>
                        ),
                      )}
                    </Masonry>
                  ) : (
                    <>
                      {availablesLangs.map((lang) => (
                        <MenuItem
                          onClick={() =>
                            navigate(
                              window.location.pathname.replace(
                                locale,
                                lang.code,
                              ),
                            )}
                        >
                          <Stack direction="row" spacing={1}>
                            <Flag
                              country={
                                getCountryByLanguage(lang.code) || lang.code
                              }
                            />
                            <Typography color="white">
                              {lang.localName}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </>
                  )}
                </Menu>
              </Box>
            </Box>
            <Box mt={1}>
              <Tabs
                component="nav"
                value={
                  activeSubmenu?.main
                    ? Object.keys(restHeaders()).findIndex((key) =>
                        activeSubmenu?.main?.includes(key),
                      )
                    : false
                }
              >
                {Object.entries(restHeaders()).map(([key, item]) => (
                  <Tab
                    label={item.label}
                    sx={{
                      color: (theme) =>
                        (contrastMenu ? theme.palette.primary.main : "white"),
                      fontSize: "16px !important",
                    }}
                    onClick={() =>
                      (item.elements
                        ? setActiveSubmenu({ main: `${key}.elements` })
                        : goToPage(item.path))}
                  />
                ))}
              </Tabs>

              <Box ml={1}>
                <ConnectButton
                  variant="contained"
                  color="secondary"
                  size="small"
                  isHovered={contrastMenu}
                  onClick={() => window.open("https://next.allfunds.com/login")}
                >
                  <Typography color="white !important">Login</Typography>
                </ConnectButton>
              </Box>
            </Box>
          </Box>
        </MenuCard>
        {(activeSubmenu || isOpenCollapsedMenu) && (
          <SubMenu sx={{ px: { sm: 10 }, py: { sm: 2 } }}>
            {isOpenCollapsedMenu ? (
              <Box className="collapsed-menu">{renderCollapsedMenu()}</Box>
            ) : (
              <Box display="flex">
                <Box>
                  <SectionLink
                    to={
                      restHeaders()[activeSubmenu?.main?.split(".")[0]]?.path ||
                      "/"
                    }
                  >
                    <img
                      alt=""
                      src={aboutUs}
                      style={{ height: "250px", borderRadius: "5px" }}
                    />
                  </SectionLink>
                </Box>
                <Box paddingLeft={2} width={385}>
                  <Stack>
                    {Object.entries(
                      _.get(restHeaders(), `${activeSubmenu.main}`, {}),
                    )?.map(([, value]) => (
                      <Box py={1}>
                        <MenuLink to={`${value.path || "/"}`}>
                          <Typography
                            variant="small"
                            sx={{ display: "inline-block" }}
                          >
                            {value.label}
                          </Typography>
                        </MenuLink>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}
          </SubMenu>
        )}
      </Box>
    </MenuContainer>
  );
}

export default Navbar;

const StyledCollapsedMenu = styled(Stack)(
  ({ theme }) => `
  ${theme.breakpoints.up("md")} {
    display: none;
  }
`,
);

const Masonry = styled(Box)`
  padding: ${({ theme }) => theme.spacing(2)};
  margin: auto;
  columns: ${({ columns }) => `${columns}`};
  column-gap: ${({ theme }) => theme.spacing(1)};
  div {
    break-inside: avoid;
  }
`;

const LinkedTypography = styled(Typography)`
  color: white;
  font-size: 16px;
  font-weight: 700;
  &:hover {
    text-decoration: ${({ path }) => `${path ? "underline" : "none"}`};
    text-decoration-color: white;
    text-decoration-thickness: 2px;
    text-underline-offset: 2px;
    cursor: ${({ path }) => `${path && "pointer"}`};
  }
`;

const MenuContainer = styled(Box)`
  width: 100%;
  height: auto;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  cursor: pointer;
  .navbar {
    margin: 0 auto;
    width: 95%;
    transition: transform 1s;
  }
  .navbar.hidden {
    transform: translateY(calc(-200px));
  }
`;

const MenuCard = styled(Box)(
  ({ theme, isHovered, activeSubmenu, isOpenCollapsedMenu }) => `
  padding: 0px 36px;
  transition: padding 300ms ease-out, background-color 300ms ease-out;
  display: flex;
  justify-content: space-between;
  border-radius: ${
    activeSubmenu || isOpenCollapsedMenu ? "0" : "0px 0px 5px 5px"
  };
  align-items: flex-start;

  ${
    isHovered
      ? `
      background: ${activeSubmenu ? "white" : "rgba(255, 255, 255, 0.9)"};
      // box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.3);
      // transition: padding 300ms ease-out, background-color 300ms ease-out;
    `
      : `
      backdrop-filter: blur(12px);
      background: rgba(255, 255, 255, 0.1);
    `
  }

  .logo {
    padding: 38px 0 27px 0;
    > img {
      width: 175px;
      transition: content 300ms ease-out;
    }
  }

  ${theme.breakpoints.down("md")} {
    padding: 0px 20px;
    align-items: center;
    .logo {
      padding: 20px 0px;
      > img {
        width: 120px;
      }
    }
  }

  .main {
    ${theme.breakpoints.down("md")} {
      display: none;
    }
    ${theme.breakpoints.up("md")} {
      display: flex;
      flex-direction: column;
      & > div {
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }
      .top {
        border-radius: 0px 0px 10px 10px;
        background: rgba(255, 255, 255, 0.25);
        display: flex;
        height: 48px;
        & > * {
          transition: color 300ms ease-out;
        }
      }
    }
  }

  * p, span, svg {
    color: ${isHovered ? theme.palette.primary.main : "white"};
  }
`,
);

const ShareData = styled(Box)(
  ({ theme, isHovered }) => `
  > div {
    padding-left: ${theme.spacing(1)};
    :first-child {
      padding-right: ${theme.spacing(1)};
      border-right: ${`1px solid ${
        isHovered ? theme.palette.primary.main : "white"
      }`};
    }
  }
`,
);

const SelectedButton = styled(Button)`
  border-radius: ${({ isSelected }) => isSelected && "5px 5px 0px 0px"};
  background: ${({ isSelected, theme }) =>
    isSelected && theme.palette.primary.main};
  p,
  span,
  svg {
    color: ${({ isSelected }) => isSelected && "white !important"};
  }
`;

const ConnectButton = styled(Button)`
  text-decoration: none;
  padding: 12px 24px;
  background: ${({ isHovered, theme }) =>
    (isHovered ? theme.palette.secondary.main : "rgba(255, 255, 255, 0.25)")};
  box-shadow: none !important;
  border-radius: 10px;
  height: 36px;
`;

const SubMenu = styled(Box)`
  transition: display 300ms ease-out;
  background-color: rgba(255, 255, 255);
  max-height: 1000px;
  overflow-y: auto;
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.3);
  border-radius: 0px 0px 5px 5px;
  .collapsed-menu {
    max-height: 500px;
    overflow: scroll;
  }
`;

const SectionLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primary.main};
`;

const MenuLink = styled(Link)`
  text-decoration: none;
  margin: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  position: relative;
  *:hover:before {
    content: "";
    border-bottom: 1px solid ${({ theme }) => theme.palette.secondary.main};
    position: absolute;
    left: 0;
    right: 0;
    bottom: -3px;
  }
`;
const StyledBox = styled(Box)`
  > *:not(:last-child) {
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.neutral.main}`};,
  }
`;

const CollapsedItem = styled(Box)`
  min-height: 40px;
  display: flex;
  align-items: center;
  width: 100%;
`;
